import type { Observation, ObservationDefinition } from "@medplum/fhirtypes";
import {
  Exam,
  Finding,
  type FindingComponentChange,
  FindingDefinition,
  type FindingRecordId,
  type FindingRecording,
  type PatientId,
} from "@repo/domain";
import { readExamRow } from "../fhir/exam-storage";
import { readFindingDefinitionRow } from "../fhir/finding-definition-storage";
import { readFindingRow, writeFindingRow } from "../fhir/finding-storage";

// The rows a finding write leaves behind, computed by the domain itself so the
// local row cannot drift from the server's (component labels included).

export function optimisticRecordedFinding(
  input: FindingRecording & { id: FindingRecordId; patient: PatientId },
  definition: ObservationDefinition,
): Observation {
  return writeFindingRow(
    new Finding(input, new FindingDefinition(readFindingDefinitionRow(definition))).snapshot(),
    null,
  );
}

export function optimisticEditedFinding(
  prior: Observation,
  definition: ObservationDefinition,
  recording: FindingRecording,
): Observation {
  const finding = Finding.restore(readFindingRow(prior));
  finding.edit(recording, new FindingDefinition(readFindingDefinitionRow(definition)));
  return writeFindingRow(finding.snapshot(), prior);
}

export function optimisticAnsweredComponent(
  prior: Observation,
  definition: ObservationDefinition,
  exams: readonly Observation[],
  change: FindingComponentChange,
): Observation {
  const finding = Finding.restore(readFindingRow(prior));
  finding.answerComponent(
    new FindingDefinition(readFindingDefinitionRow(definition)),
    change,
    exams.map((exam) => Exam.restore(readExamRow(exam))),
  );
  return writeFindingRow(finding.snapshot(), prior);
}
